import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useProgramMembershipQuery } from './common/queries/use-program-membership-query';
import {
  useAdvocateQuery,
  useAdvocateEntitlementsQuery,
} from './common/queries/use-advocate-query';
import { useChannelsQuery } from './common/queries/use-channels-query';
import { useFeatureFlagsQuery } from './common/queries/use-feature-flags-query';
import {
  useAssistantInboxQuery,
  useAssistantInboxCountQuery,
} from './common/queries/use-assistant-query';
import { useAnalytics } from './common/use-analytics';

import { appConfigOperations } from './models/app-config';
import { programOperations } from './models/program';
import { programMembershipOperations } from './models/program-membership';
import { advocateOperations } from './models/advocate';
import { channelOperations } from './models/channels';
import { assistantOperations } from './models/assistant';
import { setFeatureFlags } from './models/features/features';
import { Program } from './models/program/types';
import { BootstrapData } from './models/bootstrap/types';

interface ErrorState {
  isError: boolean;
  isDegraded: boolean;
  error?: Error;
}

/**
 * NOTE: currently we are using isFetching instead of isLoading because when the component mounts,
 * it will always try to fetch data, so isFetching will be true until the data is loaded.
 *
 * isLoading can be false when the component is mounted, if the data is already in local storage.
 * Using isLoading can lead to incorrectly showing the wrong state on the UI as the data itself is on react
 * query cache but not in redux.
 *
 * TODO: change to isLoading after removing redux state for program membership, advocate, channels, and feature flags.
 */
interface LoadingState {
  isFetching: boolean;
}

interface DataState extends LoadingState, ErrorState {
  isInitialized: boolean;
}
/**
 * Props for the PatronData component
 */
interface PatronDataProps {
  config: BootstrapData['config'];
  program: Program;
  advocateId: number;
  children: (dataState: DataState) => React.ReactNode;
}

/**
 * PatronData component - responsible for loading all necessary application data
 * and providing it to the Redux store
 */
const PatronData: React.FC<PatronDataProps> = ({
  config,
  program,
  advocateId,
  children,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Update document title when program changes
  useEffect(() => {
    if (program) {
      document.title = `${t('nav_links.home')} | ${program.name}`;
    }
  }, [program, t]);

  // Set program and app config in Redux store
  useEffect(() => {
    dispatch(appConfigOperations.setAppConfig(config));
    dispatch(programOperations.setProgram(program));
  }, [config, program, dispatch]);

  // Fetch program membership data
  const programMembershipQuery = useProgramMembershipQuery();

  // Sync program membership data to Redux when it changes
  useEffect(() => {
    if (programMembershipQuery.data) {
      dispatch(
        programMembershipOperations.setProgramMembership(
          programMembershipQuery.data
        )
      );
    }
  }, [programMembershipQuery.data, dispatch]);

  // Fetch advocate data
  const advocateQuery = useAdvocateQuery(advocateId);

  // Sync advocate data to Redux when it changes
  useEffect(() => {
    if (advocateQuery.data) {
      dispatch(advocateOperations.setAdvocate(advocateQuery.data));
    }
  }, [advocateQuery.data, dispatch]);

  // Fetch channels data
  const channelsQuery = useChannelsQuery();

  // Sync channels data to Redux when it changes
  useEffect(() => {
    if (channelsQuery.data) {
      // Type conversion needed due to different Channel interfaces
      dispatch(channelOperations.setChannels(channelsQuery.data || []));
    }
  }, [channelsQuery.data, dispatch]);

  // Fetch feature flags if program.id is available
  const featureFlagsQuery = useFeatureFlagsQuery(program?.id || '', {
    enabled: !!program?.id,
  });

  // Sync feature flags to Redux when they change
  useEffect(() => {
    if (featureFlagsQuery.data) {
      dispatch(setFeatureFlags(featureFlagsQuery.data));
    }
  }, [featureFlagsQuery.data, dispatch]);

  const isFetching =
    programMembershipQuery.isFetching ||
    advocateQuery.isFetching ||
    channelsQuery.isFetching ||
    featureFlagsQuery.isFetching;

  const isError =
    programMembershipQuery.isError ||
    advocateQuery.isError ||
    channelsQuery.isError ||
    featureFlagsQuery.isError;

  const previousData =
    !!programMembershipQuery.data &&
    !!advocateQuery.data &&
    !!channelsQuery.data &&
    !!featureFlagsQuery.data;

  const isInitialized = !isFetching && !isError;
  const isDegraded = previousData ? isError : false;

  // After initial data is loaded, fetch assistant data (if program.id is available)
  const programId = program?.id || '';
  const assistantInboxQuery = useAssistantInboxQuery(programId, advocateId, {
    enabled: isInitialized && !!program?.id,
  });

  // Sync assistant inbox data to Redux when it changes
  useEffect(() => {
    if (assistantInboxQuery.data) {
      dispatch(assistantOperations.setInbox(assistantInboxQuery.data));
    }
  }, [assistantInboxQuery.data, dispatch]);

  const assistantInboxCountQuery = useAssistantInboxCountQuery(
    programId,
    advocateId,
    {
      enabled: isInitialized && !!program?.id,
    }
  );

  // Sync assistant inbox count to Redux when it changes
  useEffect(() => {
    if (assistantInboxCountQuery.data) {
      dispatch(
        assistantOperations.setInboxCount(assistantInboxCountQuery.data.count)
      );
    }
  }, [assistantInboxCountQuery.data, dispatch]);

  // Fetch advocate entitlements
  const advocateEmail = advocateQuery.data?.email;
  const advocateEntitlementsQuery = useAdvocateEntitlementsQuery(
    advocateEmail || '',
    {
      enabled: !!advocateEmail && isInitialized,
    }
  );

  // Sync advocate entitlements to Redux when they change
  useEffect(() => {
    if (advocateEntitlementsQuery.data) {
      dispatch(
        advocateOperations.setAdvocateEntitlements(
          advocateEntitlementsQuery.data
        )
      );
    }
  }, [advocateEntitlementsQuery.data, dispatch]);

  // Set up analytics with the useAnalytics hook
  useAnalytics(
    config.sega_service,
    programMembershipQuery.data,
    program,
    advocateQuery.data,
    isInitialized
  );

  // Render children with current data state
  return (
    <>
      {children({
        isError,
        isDegraded,
        isFetching,
        isInitialized: !isFetching && !isError,
      })}
    </>
  );
};

export default PatronData;
